import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Calendar, ExternalLink, Globe, Instagram, MapPin, ShoppingBag, X } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "../lib/api";
import BbiShell from "../components/BbiShell";

/* Auto lead-capture: opens 10s after a visitor lands on a brand page (once
   per session per brand). Enquiry goes straight to the brand's email. */
function LeadPopup({ slug, brandName }) {
  const [show, setShow] = useState(false);
  const [form, setForm] = useState({ name: "", phone: "", email: "", note: "" });
  const [busy, setBusy] = useState(false);
  const key = `bbi_lead_shown_${slug}`;

  useEffect(() => {
    if (sessionStorage.getItem(key)) return;
    const t = setTimeout(() => { setShow(true); sessionStorage.setItem(key, "1"); }, 10000);
    return () => clearTimeout(t);
  }, [key]);

  if (!show) return null;

  const submit = async (e) => {
    e.preventDefault();
    if (form.phone.replace(/\D/g, "").length < 10) return toast.error("Enter a valid 10-digit phone number");
    setBusy(true);
    try {
      await api.post(`/public/brands/${slug}/lead`, form);
      toast.success(`Thanks! ${brandName} will contact you soon.`);
      setShow(false);
    } catch (err) {
      toast.error(errMsg(err, "Could not send — try again"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 grid place-items-center p-4 bg-black/60 backdrop-blur-sm" data-testid="brand-lead-popup">
      <form onSubmit={submit} className="w-full max-w-md bg-[#0B0F1E] border border-white/10 rounded-2xl p-6 relative">
        <button type="button" onClick={() => setShow(false)} className="absolute top-4 right-4 text-slate-500 hover:text-white" data-testid="lead-popup-close">
          <X className="w-5 h-5" />
        </button>
        <div className="text-white font-semibold text-lg">Interested in {brandName}?</div>
        <p className="text-xs text-slate-400 mt-1.5">Leave your details — the brand will reach out to you directly.</p>
        <div className="space-y-3 mt-5">
          <input data-testid="lead-name" placeholder="Your name (optional)" value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            className="w-full bg-white/[0.05] border border-white/10 rounded-xl px-4 py-3 text-sm text-white placeholder:text-slate-600 focus:outline-none focus:border-indigo-400/60" />
          <input data-testid="lead-phone" placeholder="Phone number *" required value={form.phone}
            onChange={(e) => setForm({ ...form, phone: e.target.value })}
            className="w-full bg-white/[0.05] border border-white/10 rounded-xl px-4 py-3 text-sm text-white placeholder:text-slate-600 focus:outline-none focus:border-indigo-400/60" />
          <input data-testid="lead-email" type="email" placeholder="Email (optional)" value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            className="w-full bg-white/[0.05] border border-white/10 rounded-xl px-4 py-3 text-sm text-white placeholder:text-slate-600 focus:outline-none focus:border-indigo-400/60" />
          <textarea data-testid="lead-note" rows={2} placeholder="Message / what are you looking for? (optional)" value={form.note}
            onChange={(e) => setForm({ ...form, note: e.target.value })}
            className="w-full bg-white/[0.05] border border-white/10 rounded-xl px-4 py-3 text-sm text-white placeholder:text-slate-600 focus:outline-none focus:border-indigo-400/60" />
        </div>
        <button data-testid="lead-submit" disabled={busy}
          className="mt-4 w-full py-3 rounded-xl bg-gradient-to-r from-indigo-500 to-fuchsia-500 text-sm font-semibold disabled:opacity-60"
          style={{ color: "#FFFFFF" }}>
          {busy ? "Sending…" : `Send to ${brandName}`}
        </button>
      </form>
    </div>
  );
}

function useJsonLd(id, obj) {
  useEffect(() => {
    if (!obj) return;
    const s = document.createElement("script");
    s.type = "application/ld+json";
    s.id = id;
    s.text = JSON.stringify(obj);
    document.head.appendChild(s);
    return () => { document.getElementById(id)?.remove(); };
  }, [id, obj]);
}

export default function BrandProfile() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get(`/public/brands/${slug}`).then(({ data }) => {
      setData(data);
      document.title = `${data.brand.name} — ${data.brand.category} Brand | BrandsByIndia`;
      const meta = document.querySelector('meta[name="description"]');
      if (meta) meta.setAttribute("content", (data.brand.tagline || data.brand.description || `${data.brand.name} on EcomCatalog AI`).slice(0, 155));
      window.scrollTo(0, 0);
    }).catch(() => setErr("Brand not found"));
  }, [slug]);

  const b = data?.brand;
  useJsonLd("brand-jsonld", b ? {
    "@context": "https://schema.org",
    "@type": "Brand",
    name: b.name,
    description: b.description || b.tagline || "",
    url: `https://ecomcatalog.ai/brands/${slug}`,
    ...(b.logo_url ? { logo: b.logo_url } : {}),
    ...(b.website ? { sameAs: [b.website, b.instagram, b.myntra_url, b.meesho_url].filter(Boolean) } : {}),
  } : null);

  if (err) return (
    <BbiShell>
      <div className="min-h-[50vh] grid place-items-center text-slate-400 text-sm" data-testid="brand-not-found">{err}</div>
    </BbiShell>
  );
  if (!data) return (
    <BbiShell>
      <div className="min-h-[50vh] grid place-items-center text-slate-500 text-sm">Loading brand…</div>
    </BbiShell>
  );

  const links = [
    b.website && { label: "Official Website", href: b.website, icon: Globe },
    b.myntra_url && { label: "Shop on Myntra", href: b.myntra_url, icon: ShoppingBag },
    b.meesho_url && { label: "Shop on Meesho", href: b.meesho_url, icon: ShoppingBag },
    b.instagram && { label: "Instagram", href: b.instagram, icon: Instagram },
  ].filter(Boolean);

  return (
    <BbiShell>
      <LeadPopup slug={slug} brandName={b.name} />
      <section className="pt-14 sm:pt-20 pb-10">
        <div className="max-w-5xl mx-auto px-5 sm:px-8">
          <Link to="/brands" className="text-xs text-slate-500 hover:text-white flex items-center gap-1.5 mb-8" data-testid="back-to-directory">
            <ArrowLeft className="w-3.5 h-3.5" /> All brands
          </Link>
          <div className="flex flex-col sm:flex-row sm:items-center gap-6">
            {b.logo_url ? (
              <img src={b.logo_url} alt={`${b.name} logo`} className="w-24 h-24 rounded-2xl object-cover bg-white shadow-xl" />
            ) : (
              <div className="w-24 h-24 rounded-2xl bg-gradient-to-br from-indigo-500 to-fuchsia-500 grid place-items-center text-white text-4xl font-bold shadow-xl">
                {b.name[0]}
              </div>
            )}
            <div>
              <div className="flex items-center gap-3 flex-wrap">
                <h1 className="font-mkt-display text-4xl sm:text-5xl font-semibold tracking-tight" data-testid="brand-profile-name">{b.name}</h1>
                <span className="px-2.5 py-1 rounded-full bg-emerald-500/15 border border-emerald-400/30 text-emerald-300 text-[10px] font-bold uppercase tracking-wider">✓ Verified</span>
              </div>
              <div className="flex items-center gap-4 mt-3 text-sm text-slate-400 flex-wrap">
                <span className="text-indigo-400 font-semibold">{b.category}</span>
                {b.city && <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" />{b.city}</span>}
              </div>
            </div>
          </div>
          {b.tagline && <p className="text-lg text-slate-300 mt-7 max-w-3xl">{b.tagline}</p>}
          {b.description && <p className="text-sm text-slate-400 mt-4 max-w-3xl leading-relaxed whitespace-pre-line">{b.description}</p>}

          {links.length > 0 && (
            <div className="flex gap-3 mt-8 flex-wrap">
              {links.map((l) => (
                <a
                  key={l.label}
                  href={l.href}
                  target="_blank"
                  rel="noopener"
                  data-testid={`brand-link-${l.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  className="flex items-center gap-2 px-4 py-2.5 rounded-full bg-white/[0.05] border border-white/10 text-sm text-white hover:border-indigo-400/50 hover:bg-white/[0.08] transition-all"
                >
                  <l.icon className="w-4 h-4 text-indigo-400" /> {l.label} <ExternalLink className="w-3 h-3 text-slate-500" />
                </a>
              ))}
            </div>
          )}
        </div>
      </section>

      <section className="pb-24">
        <div className="max-w-5xl mx-auto px-5 sm:px-8">
          <h2 className="text-lg font-semibold text-white mb-6">Articles &amp; Guides</h2>
          {data.articles.length === 0 ? (
            <div className="text-sm text-slate-500 border border-dashed border-white/10 rounded-2xl p-10 text-center" data-testid="brand-no-articles">
              Articles coming soon for {b.name}.
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 gap-5">
              {data.articles.map((a) => (
                <Link
                  key={a.slug}
                  to={`/brands/${slug}/${a.slug}`}
                  data-testid={`brand-article-${a.slug}`}
                  className="group bg-white/[0.03] border border-white/10 rounded-2xl p-6 hover:border-indigo-400/40 transition-all"
                >
                  <div className="text-white font-semibold leading-snug group-hover:text-indigo-300 transition-colors">{a.title}</div>
                  {a.meta_description && <p className="text-sm text-slate-400 mt-2.5 line-clamp-2">{a.meta_description}</p>}
                  <div className="flex items-center justify-between mt-5 text-xs text-slate-500">
                    <span className="flex items-center gap-1.5"><Calendar className="w-3 h-3" />{(a.published_at || a.created_at || "").slice(0, 10)}</span>
                    <span className="text-indigo-400 font-semibold flex items-center gap-1 group-hover:gap-2 transition-all">Read <ArrowRight className="w-3 h-3" /></span>
                  </div>
                </Link>
              ))}
            </div>
          )}

          {(data.network_articles || []).length > 0 && (
            <div className="mt-12" data-testid="brand-network-articles">
              <h2 className="text-lg font-semibold text-white mb-5">More from BrandsByIndia</h2>
              <div className="grid sm:grid-cols-3 gap-4">
                {data.network_articles.map((r) => (
                  <Link key={`${r.brand_slug}-${r.slug}`} to={`/brands/${r.brand_slug}/${r.slug}`}
                    className="group bg-white/[0.03] border border-white/10 rounded-xl p-5 hover:border-indigo-400/40 transition-all">
                    <div className="text-[10px] uppercase tracking-wider text-indigo-400 font-bold">{r.brand_name}</div>
                    <div className="text-sm text-white font-semibold mt-1.5 leading-snug line-clamp-2 group-hover:text-indigo-300 transition-colors">{r.title}</div>
                    {r.meta_description && <p className="text-xs text-slate-400 mt-2 line-clamp-2">{r.meta_description}</p>}
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>
    </BbiShell>
  );
}
